import { create } from 'zustand';
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { WarningCircle, Question } from '@phosphor-icons/react';

// --- Promise-based confirm store ---
// Usage anywhere in the app:
//   import { confirm } from '../components/ConfirmDialog';
//   if (await confirm({ title, message, danger: true })) { ... }

const useConfirmStore = create((set) => ({
  open: false,
  opts: null,
  resolver: null,
  ask: (opts) =>
    new Promise((resolve) => {
      set({ open: true, opts, resolver: resolve });
    }),
  respond: (val) => {
    set((s) => {
      if (s.resolver) s.resolver(val);
      return { open: false, opts: null, resolver: null };
    });
  },
}));

export const confirm = (opts) => useConfirmStore.getState().ask(opts);

// --- Mounted once in App.js ---
const ConfirmDialog = () => {
  const { open, opts, respond } = useConfirmStore();
  const {
    title = 'Are you sure?',
    message = '',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    altText = '',
  } = opts || {};
  const danger = opts?.danger ?? /delete|remove|archive|reject|revoke|discard|cannot be undone/i.test(String(message));

  const Icon = danger ? WarningCircle : Question;
  const iconBg = danger ? 'bg-rose-100 text-rose-600' : 'bg-[#E2F3F0] text-[#00a69c]';
  const confirmBtn = danger
    ? 'bg-rose-600 hover:bg-rose-700 text-white'
    : 'bg-[#00a69c] hover:bg-[#008c84] text-white';

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) respond(false); }}>
      <DialogContent className="max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${iconBg}`}>
              <Icon size={22} weight="fill" />
            </div>
            <span data-testid="confirm-dialog-title">{title}</span>
          </DialogTitle>
        </DialogHeader>
        {message && (
          <p className="text-sm text-[#3B5A54] leading-relaxed mt-2 whitespace-pre-line" data-testid="confirm-dialog-message">
            {message}
          </p>
        )}
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={() => respond(false)}
            className="px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm font-medium text-[#3B5A54] hover:bg-[#F4F9F7] transition"
            data-testid="confirm-dialog-cancel"
          >
            {cancelText}
          </button>
          {altText && (
            <button
              type="button"
              onClick={() => respond('alt')}
              className="px-4 py-2 border border-[#00a69c] rounded-lg text-sm font-medium text-[#00a69c] hover:bg-[#E2F3F0] transition"
              data-testid="confirm-dialog-alt"
            >
              {altText}
            </button>
          )}
          <button
            type="button"
            onClick={() => respond(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${confirmBtn}`}
            data-testid="confirm-dialog-confirm"
            autoFocus
          >
            {confirmText}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmDialog;
